/**
 * Per-tick status effect processing: damage over time, restoration over time, ability bursts
 * and beams, expiry at the end of the tick, and consumption of one-shot impulses after movement.
 */
import { SIM_DT } from '../constants.js';
import { degToRad, forwardXZ, vNormalize } from '../sim/math.js';
import { rngFloat } from '../sim/rng.js';
import { applyDamage } from './damage.js';
import { centerOf } from './state.js';
import { aimDir, aimOrigin, muzzlePosition } from './state.js';
import { defOf, emit, mech } from './tick.js';
import { otherSlot } from './types.js';
import { beamTick, spawnProjectile, spreadDirection } from './weapons.js';
import { weaponOf } from './catalog.js';
/** Launch direction for a mortar shell to land at `target` (high arc), or straight at it. */
export function mortarDirection(from, target, speed, gravity) {
    const dx = target.x - from.x;
    const dz = target.z - from.z;
    const range = Math.hypot(dx, dz);
    if (range < 1e-6 || gravity <= 0)
        return vNormalize({ x: dx, y: target.y - from.y, z: dz });
    const ratio = (gravity * range) / (speed * speed);
    // High-arc solution; fall back to 45 degrees when out of range.
    const angle = ratio >= 1 ? Math.PI / 4 : (Math.PI - Math.asin(ratio)) / 2;
    const ux = dx / range;
    const uz = dz / range;
    return { x: ux * Math.cos(angle), y: Math.sin(angle), z: uz * Math.cos(angle) };
}
/** Fires the next round of an ability burst (swarm, mortar, or straight ahead). */
export function fireBurstShot(t, slot, e) {
    const burst = e.params.burst;
    const weapon = weaponOf(t.ctx, burst.weapon);
    const spec = weapon.projectile;
    const c = mech(t, slot);
    const def = defOf(t, slot);
    const origin = aimOrigin(c.body, def, t.ctx.balance.aimHeightFraction);
    const muzzle = muzzlePosition(c.body, weapon.muzzle);
    const spread = degToRad(burst.spreadDeg);
    const enemy = otherSlot(slot);
    if (burst.pattern === 'swarmUp') {
        const dir = spreadDirection(t, { x: 0, y: 1, z: 0 }, spread);
        const start = { x: muzzle.x, y: muzzle.y + 0.5, z: muzzle.z };
        spawnProjectile(t, slot, weapon, start, dir, {
            homingTarget: mech(t, enemy).alive ? enemy : null,
        });
        return;
    }
    if (burst.pattern === 'mortar') {
        const aimed = burst.target ?? {
            x: origin.x + forwardXZ(c.body.aimYaw).x * 30,
            y: c.body.position.y,
            z: origin.z + forwardXZ(c.body.aimYaw).z * 30,
        };
        // Scatter the landing point inside a disc, then solve the exact arc for it.
        const r = burst.scatter * Math.sqrt(rngFloat(t.s.rng));
        const a = Math.PI * 2 * rngFloat(t.s.rng);
        const target = { x: aimed.x + Math.cos(a) * r, y: aimed.y, z: aimed.z + Math.sin(a) * r };
        const base = mortarDirection(muzzle, target, spec.speed, spec.gravity);
        const dir = spreadDirection(t, base, spread);
        spawnProjectile(t, slot, weapon, { x: muzzle.x, y: muzzle.y + 0.5, z: muzzle.z }, dir, {
            groundY: target.y + 0.2,
        });
        return;
    }
    const dir = spreadDirection(t, aimDir(c.body), spread);
    const start = { x: origin.x + dir.x * 1.5, y: origin.y + dir.y * 1.5, z: origin.z + dir.z * 1.5 };
    spawnProjectile(t, slot, weapon, start, dir);
}
/** Runs every effect's per-tick action for one mech. */
export function tickEffectActions(t, slot) {
    const c = mech(t, slot);
    const def = defOf(t, slot);
    for (const e of [...c.effects]) {
        if (!c.alive || !c.effects.includes(e))
            break;
        const p = e.params;
        if (p.dot) {
            const enemyPos = mech(t, otherSlot(slot)).body.position;
            applyDamage(t, {
                target: slot,
                source: e.source,
                amount: p.dot.damagePerSecond * SIM_DT,
                kind: p.dot.kind,
                location: 'torso',
                rear: false,
                position: centerOf(c.body, def),
            });
            void enemyPos;
        }
        if (p.hot) {
            if (p.hot.hp)
                c.hp = Math.min(def.hp, c.hp + p.hot.hp * SIM_DT);
            if (p.hot.shield)
                c.shield = Math.min(def.shield, c.shield + p.hot.shield * SIM_DT);
            if (p.hot.heat)
                c.heat = Math.max(0, Math.min(def.heatCap, c.heat + p.hot.heat * SIM_DT));
            if (p.hot.energy)
                c.body.energy = Math.min(def.energy, c.body.energy + p.hot.energy * SIM_DT);
        }
        if (p.burst) {
            p.burst.timer--;
            if (p.burst.timer <= 0 && p.burst.left > 0) {
                fireBurstShot(t, slot, e);
                p.burst.left--;
                p.burst.timer = p.burst.intervalTicks;
            }
            if (p.burst.left <= 0)
                e.ticksLeft = 0;
        }
        if (p.beam) {
            const weapon = weaponOf(t.ctx, p.beam.weapon);
            beamTick(t, slot, weapon, aimOrigin(c.body, def, t.ctx.balance.aimHeightFraction), aimDir(c.body), muzzlePosition(c.body, weapon.muzzle), {
                siphon: p.beam.hpPerSecond > 0 || p.beam.energyPerSecond > 0
                    ? { hpPerSecond: p.beam.hpPerSecond, energyPerSecond: p.beam.energyPerSecond }
                    : null,
            });
        }
    }
}
/** Impulses are consumed by the movement step that follows their creation. */
export function consumeImpulses(t, slot) {
    const c = mech(t, slot);
    if (!c.effects.some((e) => e.params.impulse))
        return;
    c.effects = c.effects.filter((e) => !e.params.impulse);
}
/** End of tick: count effects down and drop the expired ones. */
export function expireEffects(t, slot) {
    const c = mech(t, slot);
    if (c.effects.length === 0)
        return;
    const kept = [];
    for (const e of c.effects) {
        if (e.params.impulse) {
            // Consumed by the next movement step, whatever tick it was created in.
            kept.push(e);
            continue;
        }
        e.ticksLeft--;
        if (e.ticksLeft <= 0) {
            emit(t, { t: 'effectEnd', slot, kind: e.kind, id: e.id });
            if (e.params.dash) {
                // A dash ends at walking speed rather than carrying its burst of momentum.
                const v = c.body.velocity;
                const speed = Math.hypot(v.x, v.z);
                const limit = defOf(t, slot).speed;
                if (speed > limit)
                    c.body.velocity = { x: (v.x / speed) * limit, y: v.y, z: (v.z / speed) * limit };
            }
        }
        else {
            kept.push(e);
        }
    }
    c.effects = kept;
}
//# sourceMappingURL=effects-tick.js.map